import { isTenantSmava } from 'utils/url'
import { logAndTrackError } from 'utils/log/logAndTrackError'
import UnknownConfigError from 'error/unknownConfigError'
import { isSmava } from 'utils/affiliate'

import { LoadedConfig } from '../config.interface'
import RootStore from '../../stores/RootStore'
import { PossibleVariants } from 'utils/tracking/optimizely/types'

export const CONFIG_TEST = 'test_config'
export const CONFIG_TEST_URL_PARAMS = 'test_url_params'

export const CONFIG_SECOND_DEBTOR = 'second_debtor'
export const CONFIG_SECOND_DEBTOR_SMAVA = 'second_debtor_smava'
export const CONFIG_DDF = 'ddf'
export const CONFIG_LEAD_FORM = 'lead_form'
export const CONFIG_DDF_SMAVA = 'ddf_smava'
export const CONFIG_DDF_SMAVA_RCE_EDIT = 'ddf_smava_rce_edit_page'
export const CONFIG_DDF_RCE_EDIT = 'ddf_rce_edit_page'
export const CONFIG_DDF_CAT_4149_VAR_B = 'ddf_cat_4149_var_b'
export const CONFIG_DDF_CAT_4149_VAR_C = 'ddf_cat_4149_var_c'
export const CONFIG_DDF_CAT_4149_VAR_D = 'ddf_cat_4149_var_d'
export const CONFIG_DDF_CAT_4197_VAR_B = 'ddf_cat_4197_var_b'

export const configMap = {
  [CONFIG_TEST]: () => import('../test-config'),
  [CONFIG_TEST_URL_PARAMS]: () => import('../test-url-params'),
  [CONFIG_SECOND_DEBTOR]: () => import('../second-debtor-config'),
  [CONFIG_SECOND_DEBTOR_SMAVA]: () => import('../second-debtor-config-smava'),
  [CONFIG_DDF]: () => import('../ddf-config'),
  [CONFIG_LEAD_FORM]: () => import('../lead-form-config'),
  [CONFIG_DDF_SMAVA]: () => import('../ddf-smava-config'),
  [CONFIG_DDF_SMAVA_RCE_EDIT]: () => import('../ddf-smava-rce-edit-page'),
  [CONFIG_DDF_RCE_EDIT]: () => import('../ddf-rce-edit-page'), // used in CAT-3598
  [CONFIG_DDF_CAT_4149_VAR_B]: () => import('../ddf-cat-4149-var-b'),
  [CONFIG_DDF_CAT_4149_VAR_C]: () => import('../ddf-cat-4149-var-c'),
  [CONFIG_DDF_CAT_4149_VAR_D]: () => import('../ddf-cat-4149-var-d'),
  [CONFIG_DDF_CAT_4197_VAR_B]: () => import('../ddf-cat-4197-var-b')
}

export type ConfigName = keyof typeof configMap

export type Cat4149Variant =
  | PossibleVariants.VARIATION_B
  | PossibleVariants.VARIATION_C
  | PossibleVariants.VARIATION_D

export const CAT_4149_CONFIG_MAPPER: Record<Cat4149Variant, ConfigName> = {
  [PossibleVariants.VARIATION_B]: CONFIG_DDF_CAT_4149_VAR_B,
  [PossibleVariants.VARIATION_C]: CONFIG_DDF_CAT_4149_VAR_C,
  [PossibleVariants.VARIATION_D]: CONFIG_DDF_CAT_4149_VAR_D
}

export function doesConfigExist(name: string) {
  return Object.keys(configMap).indexOf(name) > -1
}

/**
 * We have to do it like this because Webpack needs to know the imports in advance.
 * Otherwise the config file won't be bundled correcly and cannot be found.
 *
 * @param name e.g. `classic-config`
 */
export async function getConfig(name: ConfigName): Promise<LoadedConfig> {
  const configImport = configMap[name]
  if (!configImport) {
    logAndTrackError(new UnknownConfigError(name))

    if (isTenantSmava()) {
      return (await configMap[CONFIG_DDF_SMAVA]()).default
    }

    return (await configMap[CONFIG_DDF]()).default
  }

  return (await configImport()).default
}

export async function getActiveConfig(
  affiliateId: number
): Promise<LoadedConfig> {
  if (isSmava(affiliateId)) {
    return getConfig(CONFIG_DDF_SMAVA)
  }

  return getConfig(CONFIG_DDF)
}

export function getDisplayedPages(rootStore: RootStore) {
  const {
    page: { config }
  } = rootStore

  if (!config.version) {
    return []
  }

  return Object.entries(config.pages).filter(
    ([, options]) => !options.condition || options.condition(rootStore)
  )
}

const DDF_SMAVA_CONFIGS = [
  CONFIG_DDF_SMAVA,
  CONFIG_DDF_SMAVA_RCE_EDIT,
  CONFIG_DDF_CAT_4149_VAR_D,
  CONFIG_DDF_CAT_4149_VAR_B,
  CONFIG_DDF_CAT_4149_VAR_C
]

export const CONFIGS_WITH_AGE_BASED_TIMEOUT = new Set([
  CONFIG_DDF_SMAVA_RCE_EDIT,
  CONFIG_DDF_RCE_EDIT
])

const isRceEdit = (config: string | undefined): boolean =>
  config === CONFIG_DDF_SMAVA_RCE_EDIT || config === CONFIG_DDF_RCE_EDIT

export const isConfigWithoutSaveButton = (config: string | undefined) =>
  isRceEdit(config)

export const isFFGConfigWithAutoRedirectOnDacSuccess = (
  config: string | undefined
) =>
  config === CONFIG_DDF ||
  config === CONFIG_DDF_RCE_EDIT ||
  config === CONFIG_DDF_CAT_4197_VAR_B

export const isDDFSmavaConfig = (config: string | undefined) =>
  config && DDF_SMAVA_CONFIGS.includes(config)

export const isRceEditConfig = (config: string | undefined) => isRceEdit(config)

export const ffgCustomMessageOnLastPageConfig = [CONFIG_DDF]

export const isTouchIgnoredConfig = (config: string | undefined) =>
  isRceEdit(config) || config === CONFIG_DDF
